import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { KeyRound, Copy, Ban, Loader2, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";

const NewCode = ({ onCreated }) => {
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/codes", { label, max_uses: Number(maxUses) || 1 });
      setCreated(data.code);
      setLabel("");
      onCreated();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard?.writeText(created); toast.success("Code copié."); };
  return (
    <div className="card-flat p-6">
      <p className="font-semibold">Créer un code d'accès gratuit</p>
      <p className="text-sm text-muted-foreground">Chaque code donne l'offre Pro illimitée, pour toujours, au compte qui l'active.</p>
      <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input data-testid="admin-code-label-input" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} placeholder="Note (ex. : Classe de 3e B)"
          className="h-11 flex-1 rounded-full border bg-background px-4 text-sm outline-none focus:border-primary/50" />
        <label className="flex items-center gap-2 text-sm text-muted-foreground">Utilisations
          <input data-testid="admin-code-max-uses-input" type="number" min={1} max={1000} value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
            className="h-11 w-20 rounded-full border bg-background px-3 text-sm outline-none focus:border-primary/50" />
        </label>
        <button data-testid="admin-create-code-button" disabled={busy} className="bg-brand inline-flex h-11 items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold text-white">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Générer
        </button>
      </form>
      {created && (
        <div className="mt-4 rounded-2xl border border-primary/40 bg-accent/50 p-4" data-testid="admin-created-code">
          <p className="text-xs text-muted-foreground">Copie ce code maintenant : pour des raisons de sécurité, il ne sera plus affiché en entier.</p>
          <div className="mt-2 flex items-center gap-3">
            <code className="font-mono text-lg font-bold tracking-wider" data-testid="admin-created-code-value">{created}</code>
            <button data-testid="admin-copy-code-button" onClick={copy} className="rounded-full border p-2 hover:border-primary/40"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const { user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const load = useCallback(async () => {
    try {
      const [c, u] = await Promise.all([api.get("/admin/codes"), api.get("/admin/users")]);
      setCodes(c.data);
      setUsers(u.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);
  if (!user?.is_admin) return <Navigate to="/app" replace />;

  const revokeCode = async (id) => { try { await api.delete(`/admin/codes/${id}`); toast.success("Code désactivé."); load(); } catch (e) { toast.error(errMsg(e)); } };
  const revokeUser = async (id) => { try { await api.post(`/admin/users/${id}/revoke-free`); toast.success("Accès offert retiré."); load(); } catch (e) { toast.error(errMsg(e)); } };
  const revokeStudent = async (id) => { try { await api.post(`/admin/users/${id}/revoke-student`); toast.success("Statut étudiant retiré."); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="admin-page">
      <p className="eyebrow">Administration</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">Codes d'accès & utilisateurs</h1>
      <div className="mt-8"><NewCode onCreated={load} /></div>

      <h2 className="mt-10 flex items-center gap-2 text-xl font-bold"><KeyRound className="h-5 w-5 text-primary" /> Codes ({codes.length})</h2>
      <div className="card-flat mt-4 divide-y" data-testid="admin-codes-list">
        {codes.length === 0 && <p className="p-5 text-sm text-muted-foreground">Aucun code pour l'instant.</p>}
        {codes.map((c) => (
          <div key={c.code_id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`admin-code-${c.code_id}`}>
            <code className="font-mono font-semibold">NMC-••••-••••-{c.hint}</code>
            <span className="flex-1 text-muted-foreground">{c.label || "—"} · créé le {fmtDate(c.created_at)}</span>
            <span className="text-muted-foreground">{c.uses} / {c.max_uses} utilisation(s)</span>
            {c.active ? (
              <button data-testid={`admin-revoke-code-${c.code_id}`} onClick={() => revokeCode(c.code_id)} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold text-destructive hover:border-destructive/50"><Ban className="h-3.5 w-3.5" /> Désactiver</button>
            ) : <span className="rounded-full bg-muted px-3 py-1 text-xs">Désactivé</span>}
          </div>
        ))}
      </div>

      <h2 className="mt-10 flex items-center gap-2 text-xl font-bold"><Users className="h-5 w-5 text-primary" /> Utilisateurs ({users.length})</h2>
      <div className="card-flat mt-4 divide-y" data-testid="admin-users-list">
        {users.map((u) => (
          <div key={u.user_id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`admin-user-${u.user_id}`}>
            <span className="min-w-0 flex-1"><b>{u.name}</b> <span className="text-muted-foreground">{u.email}</span></span>
            <span className="text-muted-foreground">{u.courses} cours</span>
            <span className="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground">{u.plan}{u.free_access ? " · offert" : ""}</span>
            {u.student_until && new Date(u.student_until) > new Date() && (
              <button data-testid={`admin-revoke-student-${u.user_id}`} onClick={() => revokeStudent(u.user_id)} title={u.student_institution || ""} className="rounded-full border border-emerald-500/40 px-3 py-1.5 text-xs font-semibold text-emerald-600 hover:border-destructive/50 hover:text-destructive">Étudiant · retirer</button>
            )}
            {u.free_access && <button data-testid={`admin-revoke-free-${u.user_id}`} onClick={() => revokeUser(u.user_id)} className="rounded-full border px-3 py-1.5 text-xs font-semibold text-destructive hover:border-destructive/50">Retirer l'accès</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
